import { useEffect, useMemo, useState } from "react";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

function Modal({ title, eyebrow, children, onClose }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card business-modal-card operations-modal-card polished-ops-modal">
        <header className="tazy-modal-header">
          <div>
            <span>{eyebrow}</span>
            <h3>{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["ACTIVE", "APPROVED", "COMPLETED", "PUBLISHED", "AVAILABLE", "IN_STOCK"].includes(status)) return "good";
  if (["DRAFT", "PENDING", "CONFIRMED", "IN_PROGRESS", "UNDER_REVIEW"].includes(status)) return "warn";
  if (["CANCELLED", "REJECTED", "DEFECTIVE_BATCH", "UNAVAILABLE", "OUT_OF_STOCK", "INACTIVE"].includes(status)) return "bad";
  return "neutral";
}

const branchDefaults = {
  id: null,
  name: "",
  branch_type: "SHOP",
  location: "",
  city: "",
  province: "",
  contact_person: "",
  phone: "",
  is_default: false,
  is_active: true,
};

const supplierDefaults = {
  id: null,
  name: "",
  supplier_type: "LOCAL_SUPPLIER",
  contact_person: "",
  phone: "",
  whatsapp: "",
  email: "",
  address: "",
  city: "",
  categories_supplied: "",
  payment_terms: "",
  notes: "",
  is_active: true,
};

function movementFormDefaults() {
  return {
    product: "",
    supplier: "",
    movement_type: "TRANSFER",
    quantity: 1,
    from_branch: "",
    to_branch: "",
    from_location: "",
    to_location: "",
    reference_note: "",
    status: "CONFIRMED",
  };
}

export default function BusinessWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [form, setForm] = useState({ trading_name: "", business_type: "", branch_count: 1, employee_count: "", monthly_order_volume: "NOT_SET", annual_turnover_band: "NOT_DISCLOSED", platform_scale: "NOT_SET", inventory_mode: "MARKETPLACE_ERP" });
  const [branchForm, setBranchForm] = useState(branchDefaults);
  const [supplierForm, setSupplierForm] = useState(supplierDefaults);
  const [movementForm, setMovementForm] = useState(movementFormDefaults());
  const isBusiness = user?.account_type === "BUSINESS" || user?.is_platform_staff || user?.account_type === "SUPER_ADMIN";

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await phase1Api.businessWorkspace();
      const data = res.data || {};
      setWorkspace(data);
      const profile = data.profile || {};
      setForm({
        trading_name: profile.trading_name || "",
        business_type: profile.business_type || "",
        branch_count: profile.branch_count || 1,
        employee_count: profile.employee_count ?? "",
        monthly_order_volume: profile.monthly_order_volume || "NOT_SET",
        annual_turnover_band: profile.annual_turnover_band || "NOT_DISCLOSED",
        platform_scale: profile.platform_scale || "NOT_SET",
        inventory_mode: profile.inventory_mode || "MARKETPLACE_ERP",
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Business workspace could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function update(name, value) { setForm((prev) => ({ ...prev, [name]: value })); }
  function updateBranch(name, value) { setBranchForm((prev) => ({ ...prev, [name]: value })); }
  function updateSupplier(name, value) { setSupplierForm((prev) => ({ ...prev, [name]: value })); }
  function updateMovement(name, value) { setMovementForm((prev) => ({ ...prev, [name]: value })); }

  function openBranch(item = null) {
    setBranchForm(item ? {
      id: item.id,
      name: item.name || "",
      branch_type: item.branch_type || "SHOP",
      location: item.location || "",
      city: item.city || "",
      province: item.province || "",
      contact_person: item.contact_person || "",
      phone: item.phone || "",
      is_default: Boolean(item.is_default),
      is_active: item.is_active !== false,
    } : branchDefaults);
    setBranchOpen(true);
  }

  function openSupplier(item = null) {
    setSupplierForm(item ? {
      id: item.id,
      name: item.name || "",
      supplier_type: item.supplier_type || "LOCAL_SUPPLIER",
      contact_person: item.contact_person || "",
      phone: item.phone || "",
      whatsapp: item.whatsapp || "",
      email: item.email || "",
      address: item.address || "",
      city: item.city || "",
      categories_supplied: item.categories_supplied || "",
      payment_terms: item.payment_terms || "",
      notes: item.notes || "",
      is_active: item.is_active !== false,
    } : supplierDefaults);
    setSupplierOpen(true);
  }

  async function saveProfile(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      await phase1Api.updateBusinessWorkspace({
        ...form,
        branch_count: Number(form.branch_count || 1),
        employee_count: form.employee_count === "" ? null : Number(form.employee_count),
      });
      setMessage("Business operations profile updated.");
      setEditOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Business profile could not be saved.");
    }
  }

  async function saveBranch(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = { ...branchForm };
      delete payload.id;
      if (branchForm.id) await phase1Api.updateBranch(branchForm.id, payload);
      else await phase1Api.createBranch(payload);
      setMessage(branchForm.id ? "Branch updated." : "Branch added to business operations.");
      setBranchOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Branch could not be saved.");
    }
  }

  async function saveSupplier(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = { ...supplierForm };
      delete payload.id;
      if (supplierForm.id) await phase1Api.updateSupplier(supplierForm.id, payload);
      else await phase1Api.createSupplier(payload);
      setMessage(supplierForm.id ? "Supplier updated." : "Supplier added to business operations.");
      setSupplierOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Supplier could not be saved.");
    }
  }

  async function createMovement(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = { ...movementForm, quantity: Number(movementForm.quantity) || 1 };
      ["product", "supplier", "from_branch", "to_branch"].forEach((key) => { if (!payload[key]) delete payload[key]; });
      await phase1Api.createGoodsMovement(payload);
      setMessage("Goods movement recorded successfully.");
      setMovementOpen(false);
      setMovementForm(movementFormDefaults());
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Goods movement could not be recorded.");
    }
  }

  const profile = workspace?.profile || {};
  const products = workspace?.products || [];
  const movements = workspace?.goods_movements || [];
  const branches = workspace?.branches || [];
  const suppliers = workspace?.suppliers || [];
  const operations = workspace?.operations || {};
  const restricted = products.filter((p) => ["OUT_OF_STOCK", "UNAVAILABLE", "DEFECTIVE_BATCH"].includes(p.stock_status));
  const available = products.filter((p) => ["AVAILABLE", "IN_STOCK"].includes(p.stock_status));

  const stockByStatus = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.stock_status || "AVAILABLE"] = (map[p.stock_status || "AVAILABLE"] || 0) + 1; });
    return Object.entries(map);
  }, [products]);

  if (!isBusiness) {
    return (
      <div className="portal-shell-v2">
        <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
        <main className="portal-main-v2"><PortalTopbar user={user} onLogout={logout} /><section className="workspace-card"><h2>Business workspace unavailable</h2><p>This area is for business accounts.</p></section></main>
      </div>
    );
  }

  return (
    <div className={`portal-shell-v2 ${collapsed ? "sidebar-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main className="portal-main-v2 workspace-main business-ops-workspace-main phase1n-business-workspace">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="business-ops-hero phase1n-ops-hero">
          <div>
            <span>BUSINESS OPERATIONS</span>
            <h1>{profile.trading_name || user?.business_name || "Business workspace"}</h1>
            <p>Operational control for larger shops: branches, suppliers, product catalogue, inventory status and goods movement records.</p>
          </div>
          <div className="business-ops-actions phase1n-action-stack">
            <button type="button" onClick={() => setMovementOpen(true)}>Record goods movement</button>
            <button type="button" className="secondary" onClick={() => openBranch()}>Add branch</button>
            <button type="button" className="secondary" onClick={() => openSupplier()}>Add supplier</button>
          </div>
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        {loading ? <div className="workspace-card">Loading business workspace...</div> : (
          <>
            <section className="business-ops-kpi-grid phase1n-kpi-grid">
              <article><span>Catalogue</span><strong>{products.length}</strong><em>Products linked</em></article>
              <article><span>Branches</span><strong>{branches.length}</strong><em>Shop / warehouse points</em></article>
              <article><span>Suppliers</span><strong>{suppliers.length}</strong><em>Manufacturers / distributors</em></article>
              <article><span>Movements</span><strong>{operations.movement_records ?? movements.length}</strong><em>Stock activity records</em></article>
            </section>

            <section className="business-ops-grid phase1n-ops-grid">
              <article className="workspace-card business-ops-panel phase1n-panel span-2">
                <div className="workspace-section-title"><span>BRANCH NETWORK</span><h2>Branches and stock points</h2></div>
                <p className="soft-text">Branches allow supermarkets and larger shops to track stock movement between head office, warehouses, shops and pickup points.</p>
                <div className="phase1n-table-list">
                  {branches.length ? branches.map((branch) => (
                    <button key={branch.id} type="button" className="phase1n-list-row" onClick={() => openBranch(branch)}>
                      <div><strong>{branch.name}</strong><span>{branch.branch_type?.replaceAll("_", " ")} • {branch.city || branch.location || "Location not set"}</span></div>
                      <em>{branch.is_default ? "Default" : branch.is_active ? "Active" : "Inactive"}</em>
                    </button>
                  )) : <div className="records-empty soft-empty">No branches yet. Add the main branch or warehouse first.</div>}
                </div>
              </article>

              <article className="workspace-card business-ops-panel phase1n-panel span-2">
                <div className="workspace-section-title"><span>SUPPLIER NETWORK</span><h2>Suppliers and manufacturers</h2></div>
                <p className="soft-text">Suppliers can be manufacturers, wholesalers, distributors, importers or local suppliers. They are linked to goods received and stock movement.</p>
                <div className="phase1n-table-list">
                  {suppliers.length ? suppliers.map((supplier) => (
                    <button key={supplier.id} type="button" className="phase1n-list-row" onClick={() => openSupplier(supplier)}>
                      <div><strong>{supplier.name}</strong><span>{supplier.supplier_type?.replaceAll("_", " ")} • {supplier.categories_supplied || "Categories not set"}</span></div>
                      <em>{supplier.is_active ? "Active" : "Inactive"}</em>
                    </button>
                  )) : <div className="records-empty soft-empty">No suppliers yet. Add wholesalers, manufacturers or distributors used by the business.</div>}
                </div>
              </article>

              <article className="workspace-card business-ops-panel phase1n-panel span-2">
                <div className="workspace-section-title"><span>GOODS MOVEMENT</span><h2>Movement control</h2></div>
                <p className="soft-text">Record stock received, internal transfers, dispatches, returns, adjustments and defective batches against products, suppliers and branches.</p>
                <div className="movement-list-preview phase1n-movement-list">
                  {movements.length ? movements.slice(0, 8).map((item) => (
                    <div key={item.id} className="movement-preview-row">
                      <div><strong>{item.code}</strong><span>{item.product_title || "General movement"}</span></div>
                      <em>{item.movement_type?.replaceAll("_", " ")}</em>
                      <b>{item.quantity}</b>
                      <span className={`status-chip ${statusTone(item.status)}`}>{item.status}</span>
                    </div>
                  )) : <div className="records-empty soft-empty">No movement records yet.</div>}
                </div>
              </article>

              <article className="workspace-card business-ops-panel phase1n-panel">
                <div className="workspace-section-title"><span>STOCK HEALTH</span><h2>Availability control</h2></div>
                <p className="soft-text">Buyers only see availability. Exact quantities stay private to the business.</p>
                <div className="stock-health-list">
                  {stockByStatus.length ? stockByStatus.map(([status, count]) => (
                    <div key={status}><span>{status.replaceAll("_", " ")}</span><strong>{count}</strong></div>
                  )) : <div>No products yet.</div>}
                </div>
                <a href="/portal/products" className="primary-action-btn as-link">Manage catalogue</a>
              </article>

              <article className="workspace-card business-ops-panel phase1n-panel">
                <div className="workspace-section-title"><span>OPERATIONS PROFILE</span><h2>Business setup</h2></div>
                <dl className="profile-summary-grid business-profile-metrics">
                  <div><dt>Business type</dt><dd>{profile.business_type || "—"}</dd></div>
                  <div><dt>Branches</dt><dd>{branches.length || profile.branch_count || 1}</dd></div>
                  <div><dt>Package guidance</dt><dd>{profile.package_guidance?.recommended_package || "Starter"}</dd></div>
                  <div><dt>Suppliers</dt><dd>{suppliers.length}</dd></div>
                  <div><dt>Status</dt><dd>{profile.status || "PENDING"}</dd></div>
                </dl>
                <button type="button" className="secondary-workspace-btn" onClick={() => setEditOpen(true)}>Edit profile</button>
              </article>
            </section>
          </>
        )}
      </main>

      {editOpen && (
        <Modal title="Edit operations profile" eyebrow="BUSINESS SETUP" onClose={() => setEditOpen(false)}>
          <form className="modal-form-grid phase1n-form-grid" onSubmit={saveProfile}>
            <div><label>Trading name</label><input value={form.trading_name} onChange={(e) => update("trading_name", e.target.value)} /></div>
            <div><label>Business type</label><input value={form.business_type} onChange={(e) => update("business_type", e.target.value)} placeholder="Supermarket, hardware, wholesaler..." /></div>
            <div><label>Estimated branch count</label><input type="number" min="1" value={form.branch_count} onChange={(e) => update("branch_count", e.target.value)} /></div>
            <div><label>Number of employees</label><input type="number" min="0" value={form.employee_count} onChange={(e) => update("employee_count", e.target.value)} placeholder="Optional" /></div>
            <div><label>Expected monthly orders</label><select value={form.monthly_order_volume} onChange={(e) => update("monthly_order_volume", e.target.value)}><option value="NOT_SET">Not sure yet</option><option value="0_20">0 to 20</option><option value="21_100">21 to 100</option><option value="101_500">101 to 500</option><option value="500_PLUS">More than 500</option></select></div>
            <div><label>Annual turnover band</label><select value={form.annual_turnover_band} onChange={(e) => update("annual_turnover_band", e.target.value)}><option value="NOT_DISCLOSED">Prefer not to disclose</option><option value="UNDER_10000">Under US$10,000</option><option value="10000_50000">US$10,000 to US$50,000</option><option value="50001_250000">US$50,001 to US$250,000</option><option value="250001_PLUS">Above US$250,000</option></select></div>
            <div><label>Your business size</label><select value={form.platform_scale} onChange={(e) => update("platform_scale", e.target.value)}><option value="NOT_SET">Let TAZY recommend</option><option value="MICRO">Micro</option><option value="SMALL">Small</option><option value="MEDIUM">Medium</option><option value="LARGE">Large / enterprise</option></select><small>Used only to guide package choice; it is not a legal or tax classification.</small></div>
            <div><label>Inventory mode</label><select value={form.inventory_mode} onChange={(e) => update("inventory_mode", e.target.value)}><option value="MARKETPLACE_ERP">Marketplace ERP</option><option value="CATALOGUE_ONLY">Catalogue only</option><option value="EXTERNAL_SYSTEM">External system later</option></select></div>
            <button type="submit">Save operations profile</button>
          </form>
        </Modal>
      )}

      {branchOpen && (
        <Modal title={branchForm.id ? "Edit branch" : "Add branch"} eyebrow="BRANCH NETWORK" onClose={() => setBranchOpen(false)}>
          <form className="modal-form-grid phase1n-form-grid" onSubmit={saveBranch}>
            <div><label>Branch name</label><input value={branchForm.name} onChange={(e) => updateBranch("name", e.target.value)} placeholder="Main branch / Warehouse" required /></div>
            <div><label>Branch type</label><select value={branchForm.branch_type} onChange={(e) => updateBranch("branch_type", e.target.value)}><option value="HEAD_OFFICE">Head office</option><option value="SHOP">Shop / retail branch</option><option value="WAREHOUSE">Warehouse</option><option value="PICKUP_POINT">Pickup point</option><option value="DISTRIBUTION">Distribution point</option><option value="OTHER">Other</option></select></div>
            <div className="full"><label>Location / address</label><input value={branchForm.location} onChange={(e) => updateBranch("location", e.target.value)} /></div>
            <div><label>City</label><input value={branchForm.city} onChange={(e) => updateBranch("city", e.target.value)} /></div>
            <div><label>Province</label><input value={branchForm.province} onChange={(e) => updateBranch("province", e.target.value)} /></div>
            <div><label>Contact person</label><input value={branchForm.contact_person} onChange={(e) => updateBranch("contact_person", e.target.value)} /></div>
            <div><label>Phone</label><input value={branchForm.phone} onChange={(e) => updateBranch("phone", e.target.value)} /></div>
            <label className="modal-check full"><input type="checkbox" checked={branchForm.is_default} onChange={(e) => updateBranch("is_default", e.target.checked)} /> Set as default branch</label>
            <label className="modal-check full"><input type="checkbox" checked={branchForm.is_active} onChange={(e) => updateBranch("is_active", e.target.checked)} /> Branch is active</label>
            <button type="submit">{branchForm.id ? "Update branch" : "Save branch"}</button>
          </form>
        </Modal>
      )}

      {supplierOpen && (
        <Modal title={supplierForm.id ? "Edit supplier" : "Add supplier"} eyebrow="SUPPLIER NETWORK" onClose={() => setSupplierOpen(false)}>
          <form className="modal-form-grid phase1n-form-grid" onSubmit={saveSupplier}>
            <div><label>Supplier name</label><input value={supplierForm.name} onChange={(e) => updateSupplier("name", e.target.value)} placeholder="Manufacturer / wholesaler name" required /></div>
            <div><label>Supplier type</label><select value={supplierForm.supplier_type} onChange={(e) => updateSupplier("supplier_type", e.target.value)}><option value="MANUFACTURER">Manufacturer</option><option value="WHOLESALER">Wholesaler</option><option value="DISTRIBUTOR">Distributor</option><option value="IMPORTER">Importer</option><option value="LOCAL_SUPPLIER">Local supplier</option><option value="SERVICE_PROVIDER">Service provider</option><option value="OTHER">Other</option></select></div>
            <div><label>Contact person</label><input value={supplierForm.contact_person} onChange={(e) => updateSupplier("contact_person", e.target.value)} /></div>
            <div><label>Phone</label><input value={supplierForm.phone} onChange={(e) => updateSupplier("phone", e.target.value)} /></div>
            <div><label>Supplier phone (private record)</label><input value={supplierForm.whatsapp} onChange={(e) => updateSupplier("whatsapp", e.target.value)} /></div>
            <div><label>Email</label><input type="email" value={supplierForm.email} onChange={(e) => updateSupplier("email", e.target.value)} /></div>
            <div className="full"><label>Address</label><input value={supplierForm.address} onChange={(e) => updateSupplier("address", e.target.value)} /></div>
            <div><label>City</label><input value={supplierForm.city} onChange={(e) => updateSupplier("city", e.target.value)} /></div>
            <div><label>Categories supplied</label><input value={supplierForm.categories_supplied} onChange={(e) => updateSupplier("categories_supplied", e.target.value)} placeholder="Groceries, hardware, electronics..." /></div>
            <div className="full"><label>Payment terms</label><input value={supplierForm.payment_terms} onChange={(e) => updateSupplier("payment_terms", e.target.value)} placeholder="Cash, 7 days, monthly account..." /></div>
            <div className="full"><label>Notes</label><textarea rows="3" value={supplierForm.notes} onChange={(e) => updateSupplier("notes", e.target.value)} /></div>
            <label className="modal-check full"><input type="checkbox" checked={supplierForm.is_active} onChange={(e) => updateSupplier("is_active", e.target.checked)} /> Supplier is active</label>
            <button type="submit">{supplierForm.id ? "Update supplier" : "Save supplier"}</button>
          </form>
        </Modal>
      )}

      {movementOpen && (
        <Modal title="Record goods movement" eyebrow="GOODS MOVEMENT" onClose={() => setMovementOpen(false)}>
          <form className="modal-form-grid phase1n-form-grid" onSubmit={createMovement}>
            <div><label>Product</label><select value={movementForm.product} onChange={(e) => updateMovement("product", e.target.value)}><option value="">General / not linked</option>{products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></div>
            <div><label>Supplier</label><select value={movementForm.supplier} onChange={(e) => updateMovement("supplier", e.target.value)}><option value="">No supplier</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div><label>Movement type</label><select value={movementForm.movement_type} onChange={(e) => updateMovement("movement_type", e.target.value)}><option value="RECEIVED">Stock received</option><option value="TRANSFER">Internal transfer</option><option value="DISPATCH">Dispatch</option><option value="RETURN">Return</option><option value="ADJUSTMENT">Adjustment</option><option value="DEFECTIVE">Defective batch</option></select></div>
            <div><label>Quantity</label><input type="number" min="1" value={movementForm.quantity} onChange={(e) => updateMovement("quantity", e.target.value)} /></div>
            <div><label>From branch</label><select value={movementForm.from_branch} onChange={(e) => updateMovement("from_branch", e.target.value)}><option value="">None</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
            <div><label>To branch</label><select value={movementForm.to_branch} onChange={(e) => updateMovement("to_branch", e.target.value)}><option value="">None</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
            <div><label>From location note</label><input value={movementForm.from_location} onChange={(e) => updateMovement("from_location", e.target.value)} placeholder="Supplier / warehouse / branch" /></div>
            <div><label>To location note</label><input value={movementForm.to_location} onChange={(e) => updateMovement("to_location", e.target.value)} placeholder="Branch / customer dispatch" /></div>
            <div><label>Status</label><select value={movementForm.status} onChange={(e) => updateMovement("status", e.target.value)}><option value="DRAFT">Draft</option><option value="CONFIRMED">Confirmed</option><option value="IN_PROGRESS">In progress</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option></select></div>
            <div className="full"><label>Reference note</label><input value={movementForm.reference_note} onChange={(e) => updateMovement("reference_note", e.target.value)} placeholder="Invoice, batch, dispatch note" /></div>
            <button type="submit">Save movement</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
